// @flow
import React, {useState, useEffect} from "react";
import {Form, Select, Button, message, Spin, Upload, Tabs, Divider} from "antd";
import {useTranslation, Trans} from "react-i18next";
import JSZip from "jszip";
import {courierContractService} from "../../../../services/courierContractService";
import {UploadOutlined, FileZipOutlined} from "@ant-design/icons";
import {useFormik} from "formik";
import {StyledModal, NoteBox} from "./UploadContractModal.styled";

const {Option} = Select;
const {TabPane} = Tabs;

// Hizmet Sözleşmesi için sekmeli (zip) yükleme desteklenir
const SERVICE_AGREEMENT = "courierServiceAgreement";
const MAX_FILES = 3;
const MAX_ZIP_FILES = 20;

type Props = {
    visible: boolean,
    onCancel: () => void,
    onSuccess: () => void,
    courierId: string,
    courierData: Object
};

export const UploadContractModal = ({
    visible,
    onCancel,
    onSuccess,
    courierId,
    courierData
}: Props) => {
    const {t} = useTranslation(["courierContracts"]);
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Load templates when modal opens
    useEffect(() => {
        if (visible) {
            loadTemplates();
        }
    }, [visible]);

    const loadTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const response = await courierContractService.getCourierContractTemplates(courierId);
            if (response.data && response.data.success) {
                setTemplates(response.data.result.data || []);
            } else {
                console.error("Template loading failed:", response);
                setTemplates([]);
            }
        } catch (error) {
            console.error("Template loading error:", error);
            setTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const uploadFormik = useFormik({
        initialValues: {
            selectedFiles: [],
            selectedZipFiles: [],
            selectedTemplateId: null,
            activeTab: "file"
        },
        onSubmit: async values => {
            try {
                setLoading(true);

                const isZip = isServiceAgreement && values.activeTab === "zip";
                let filesToUpload = values.selectedFiles;

                if (isZip) {
                    // Seçilen dosyalar frontend'de tek bir zip dosyasına paketlenir
                    const zip = new JSZip();
                    values.selectedZipFiles.forEach(f => {
                        const raw = f.originFileObj || f;
                        zip.file(raw.name, raw);
                    });
                    const blob = await zip.generateAsync({type: "blob"});
                    const templateName = selectedTemplate?.displayName || "contract";
                    const zipFile = new File([blob], `${templateName}.zip`, {
                        type: "application/zip"
                    });
                    filesToUpload = [zipFile];
                }

                const response = await courierContractService.uploadCourierContract(
                    courierId,
                    values.selectedTemplateId,
                    filesToUpload
                );

                if (response.data && response.data.success) {
                    message.success(t("messages.contractUploaded"));
                    uploadFormik.resetForm();
                    onSuccess();
                } else {
                    message.error(response.data?.message || t("messages.contractUploadFailed"));
                }
            } catch (error) {
                console.error("Contract upload error:", error);
                message.error(error.message || t("messages.contractUploadFailed"));
            } finally {
                setLoading(false);
            }
        }
    });

    const selectedTemplate = templates.find(
        tpl => tpl._id === uploadFormik.values.selectedTemplateId
    );
    const isServiceAgreement = selectedTemplate?.contractName === SERVICE_AGREEMENT;
    // Hizmet Sözleşmesi dışındaki şablonlar her zaman tek dosya modunda çalışır
    const mode = isServiceAgreement ? uploadFormik.values.activeTab : "file";
    const isZipMode = mode === "zip";
    const maxFiles = isZipMode ? MAX_ZIP_FILES : MAX_FILES;
    const currentFilesField = isZipMode ? "selectedZipFiles" : "selectedFiles";
    const currentFiles = uploadFormik.values[currentFilesField];

    const handleTemplateChange = value => {
        uploadFormik.setFieldValue("selectedTemplateId", value);
        // Şablon değişince sekme ve zip seçimlerini sıfırla ki tutarsız state kalmasın
        uploadFormik.setFieldValue("activeTab", "file");
        uploadFormik.setFieldValue("selectedZipFiles", []);
    };

    const handleTabChange = key => {
        uploadFormik.setFieldValue("activeTab", key);
    };

    const handleCancel = () => {
        uploadFormik.resetForm();
        onCancel();
    };

    const handleBeforeUpload = (file, fileList) => {
        const current = uploadFormik.values[currentFilesField];
        const remainingSlots = maxFiles - current.length;
        const warningMsg = isZipMode
            ? t("messages.maxZipFilesWarning")
            : t("messages.maxFilesWarning");
        if (remainingSlots <= 0) {
            message.warning(warningMsg);
            return false;
        }
        if (file.uid !== fileList[fileList.length - 1].uid) {
            return false;
        }
        const toAdd = fileList.slice(0, remainingSlots);
        if (fileList.length > remainingSlots) {
            message.warning(warningMsg);
        }
        uploadFormik.setFieldValue(currentFilesField, [...current, ...toAdd]);
        return false;
    };

    const handleRemove = file => {
        const updated = uploadFormik.values[currentFilesField].filter(f => f.uid !== file.uid);
        uploadFormik.setFieldValue(currentFilesField, updated);
    };

    const renderUpload = ({isZip}) => {
        const buttonLabel = isZip ? t("modal.selectFilesZip") : t("modal.selectFiles");
        const infoKey = isZip ? "messages.zipFilesInfo" : "messages.maxFilesInfo";
        const limit = isZip ? MAX_ZIP_FILES : MAX_FILES;
        const fieldName = isZip ? "selectedZipFiles" : "selectedFiles";
        const files = uploadFormik.values[fieldName];
        return (
            <>
                <Upload
                    multiple
                    beforeUpload={handleBeforeUpload}
                    maxCount={limit}
                    fileList={files}
                    onRemove={handleRemove}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                >
                    <Button
                        icon={isZip ? <FileZipOutlined /> : <UploadOutlined />}
                        disabled={files.length >= limit}
                    >
                        {buttonLabel}
                    </Button>
                </Upload>

                <NoteBox>
                    <p>
                        <strong>Not:</strong>{" "}
                        <Trans t={t} i18nKey={infoKey} components={{b: <strong />}} />
                    </p>
                </NoteBox>
            </>
        );
    };

    return (
        <StyledModal
            title={t("modal.uploadContract")}
            visible={visible}
            onCancel={handleCancel}
            width={600}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    {t("modal.cancel")}
                </Button>,
                <Button
                    key="upload"
                    type="primary"
                    loading={loading}
                    onClick={uploadFormik.handleSubmit}
                    disabled={!currentFiles.length || !uploadFormik.values.selectedTemplateId}
                >
                    {t("modal.upload")}
                </Button>
            ]}
        >
            <Spin spinning={loadingTemplates}>
                <Form layout="vertical">
                    <Form.Item label={t("modal.selectTemplate")} required>
                        <Select
                            placeholder={t("modal.selectTemplate")}
                            onChange={handleTemplateChange}
                            value={uploadFormik.values.selectedTemplateId}
                        >
                            {templates.map(template => (
                                <Option key={template._id} value={template._id}>
                                    {template.displayName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider />

                    {isServiceAgreement ? (
                        <Tabs activeKey={uploadFormik.values.activeTab} onChange={handleTabChange}>
                            <TabPane tab={t("modal.tabFile")} key="file">
                                {renderUpload({isZip: false})}
                            </TabPane>
                            <TabPane tab={t("modal.tabZip")} key="zip">
                                {renderUpload({isZip: true})}
                            </TabPane>
                        </Tabs>
                    ) : (
                        renderUpload({isZip: false})
                    )}
                </Form>
            </Spin>
        </StyledModal>
    );
};
